import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Alert from '../../components/ui/Alert';
import Badge from '../../components/ui/Badge';
import { ArrowLeft, Plus, Car, Truck, RefreshCw, Search, X, Clock } from 'lucide-react';
import { parkingService } from '../../services';
import type { ParkingSpot } from '../../types';
import type { SpotDetails, ReleaseSpotResponse } from '../../services/parking.service';

const ParkingSpotsPage: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const navigate = useNavigate();
  
  const [spots, setSpots] = useState<ParkingSpot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Add spots form
  const [showAddForm, setShowAddForm] = useState(false);
  const [spotPrefix, setSpotPrefix] = useState('');
  const [spotStartNumber, setSpotStartNumber] = useState('1');
  const [spotCount, setSpotCount] = useState('10');
  const [spotType, setSpotType] = useState<string>('car');
  const [spotSection, setSpotSection] = useState('');
  const [addingSpots, setAddingSpots] = useState(false);
  
  // Filter/Search
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  // Park Modal State
  const [showParkModal, setShowParkModal] = useState(false);
  const [selectedSpotForPark, setSelectedSpotForPark] = useState<ParkingSpot | null>(null);
  const [parkVehiclePlate, setParkVehiclePlate] = useState('');
  const [parkVehicleType, setParkVehicleType] = useState<'car' | 'motorcycle' | 'truck' | 'trailer'>('car');
  const [parkDriverName, setParkDriverName] = useState('');
  const [parkNotes, setParkNotes] = useState('');
  const [isParking, setIsParking] = useState(false);

  // Release Modal State
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [selectedSpotForRelease, setSelectedSpotForRelease] = useState<SpotDetails | null>(null);
  const [isReleasing, setIsReleasing] = useState(false);
  const [isLoadingSpotDetails, setIsLoadingSpotDetails] = useState(false);

  // Release Summary Modal State
  const [showReleaseSummary, setShowReleaseSummary] = useState(false);
  const [releaseSummaryData, setReleaseSummaryData] = useState<ReleaseSpotResponse | null>(null);

  const fetchLotAndSpots = useCallback(async () => {
    if (!lotId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch spots
      const spotsResponse = await parkingService.getSpots(parseInt(lotId));
      const spotsData = Array.isArray(spotsResponse) ? spotsResponse : (spotsResponse.data || []);
      setSpots(spotsData);
    } catch (err) {
      console.error('Failed to fetch spots:', err);
      setError('Failed to load parking spots');
    } finally {
      setIsLoading(false);
    }
  }, [lotId]);

  useEffect(() => {
    if (lotId) {
      fetchLotAndSpots();
    }
  }, [lotId, fetchLotAndSpots]);

  const handleAddSpots = async () => {
    if (!lotId || !spotCount) return;
    
    try {
      setAddingSpots(true);
      setError(null);
      
      const spotsToCreate = [];
      const count = parseInt(spotCount);
      const start = parseInt(spotStartNumber) || 1;
      
      for (let i = 0; i < count; i++) {
        const spotNumber = spotPrefix 
          ? `${spotPrefix}${(start + i).toString().padStart(3, '0')}`
          : (start + i).toString();
        
        const spotData: Record<string, any> = {
          spot_number: spotNumber,
          spot_type: spotType,
        };
        
        if (spotSection) {
          spotData.row = spotSection;
        }
        
        spotsToCreate.push(spotData);
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/v2'}/camera-security/parking/lots/${lotId}/spots/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({ spots: spotsToCreate }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail ? JSON.stringify(errorData.detail) : 'Failed to add spots');
      }
      
      setSuccessMessage(`${count} parking spots added successfully!`);
      setShowAddForm(false);
      setSpotPrefix('');
      setSpotStartNumber('1');
      setSpotCount('10');
      setSpotSection('');
      
      // Refresh spots
      fetchLotAndSpots();
      
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add spots');
    } finally {
      setAddingSpots(false);
    }
  };

  const handleDeleteSpot = async (spotId: number) => {
    if (!window.confirm('Are you sure you want to delete this spot?')) {
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/v2'}/camera-security/parking/spots/${spotId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete spot');
      }
      
      setSuccessMessage('Spot deleted successfully!');
      fetchLotAndSpots();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete spot');
    }
  };

  // Handle clicking on an available spot to park
  const handleSpotClick = async (spot: ParkingSpot) => {
    const spotId = (spot as any).id || spot.spot_id;
    const spotStatus = spot.status || 'available';

    if (spotStatus === 'available') {
      // Show park modal
      setSelectedSpotForPark(spot);
      setParkVehiclePlate('');
      setParkVehicleType(spot.spot_type === 'truck' ? 'truck' : spot.spot_type === 'trailer' ? 'trailer' : spot.spot_type === 'motorcycle' ? 'motorcycle' : 'car');
      setParkDriverName('');
      setParkNotes('');
      setShowParkModal(true);
    } else if (spotStatus === 'occupied') {
      // Fetch spot details and show release modal
      try {
        setIsLoadingSpotDetails(true);
        const details = await parkingService.getSpotDetails(spotId);
        setSelectedSpotForRelease(details);
        setShowReleaseModal(true);
      } catch (err) {
        // Fallback to basic info if details fetch fails
        setSelectedSpotForRelease({
          ...spot,
          occupied_since: undefined,
          current_session_id: undefined
        } as SpotDetails);
        setShowReleaseModal(true);
      } finally {
        setIsLoadingSpotDetails(false);
      }
    }
  };

  // Handle parking a vehicle
  const handleParkVehicle = async () => {
    if (!selectedSpotForPark) return;
    
    const spotId = (selectedSpotForPark as any).id || selectedSpotForPark.spot_id;
    
    try {
      setIsParking(true);
      setError(null);

      await parkingService.parkInSpot(spotId, {
        vehicle_plate: parkVehiclePlate.toUpperCase() || undefined,
        vehicle_type: parkVehicleType,
        driver_name: parkDriverName || undefined,
        notes: parkNotes || undefined
      });

      setSuccessMessage(`Vehicle parked in spot ${selectedSpotForPark.spot_number}!`);
      setShowParkModal(false);
      setSelectedSpotForPark(null);
      fetchLotAndSpots();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to park vehicle');
    } finally {
      setIsParking(false);
    }
  };

  // Handle releasing a spot
  const handleReleaseSpot = async () => {
    if (!selectedSpotForRelease) return;
    
    const spotId = (selectedSpotForRelease as any).id || selectedSpotForRelease.spot_id;
    
    try {
      setIsReleasing(true);
      setError(null);

      const response = await parkingService.releaseSpot(spotId);

      setShowReleaseModal(false);
      setSelectedSpotForRelease(null);
      
      // Show release summary
      setReleaseSummaryData(response);
      setShowReleaseSummary(true);
      
      fetchLotAndSpots();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to release spot');
    } finally {
      setIsReleasing(false);
    }
  };

  // Format time for display
  const formatTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getSpotTypeIcon = (type: string) => {
    switch (type) {
      case 'truck':
      case 'trailer':
        return <Truck size={16} className="text-gray-600" />;
      default:
        return <Car size={16} className="text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <Badge variant="success" size="sm">Available</Badge>;
      case 'occupied':
        return <Badge variant="danger" size="sm">Occupied</Badge>;
      case 'reserved':
        return <Badge variant="warning" size="sm">Reserved</Badge>;
      case 'maintenance':
        return <Badge variant="info" size="sm">Maintenance</Badge>;
      default:
        return <Badge variant="default" size="sm">{status}</Badge>;
    }
  };

  // Filter spots
  const filteredSpots = spots.filter(spot => {
    const spotStatus = spot.status || 'available';
    const spotNumber = spot.spot_number || '';
    const plate = (spot as any).current_vehicle_plate || spot.current_plate || '';
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!spotNumber.toLowerCase().includes(search) && !plate.toLowerCase().includes(search)) {
        return false;
      }
    }
    
    // Status filter
    if (filterStatus !== 'all' && spotStatus !== filterStatus) {
      return false;
    }
    
    // Type filter
    if (filterType !== 'all' && spot.spot_type !== filterType) {
      return false;
    }
    
    return true;
  });

  // Stats
  const totalSpots = spots.length;
  const availableSpots = spots.filter(s => s.status === 'available').length;
  const occupiedSpots = spots.filter(s => s.status === 'occupied').length;

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/parking')}
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Parking Lots
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Manage Parking Spots</h1>
              <p className="text-sm text-text-muted">Lot ID: {lotId}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchLotAndSpots}
              disabled={isLoading}
            >
              <RefreshCw size={16} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={() => setShowAddForm(!showAddForm)}>
              <Plus size={16} className="mr-2" />
              Add Spots
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="error" message={error} onClose={() => setError(null)} />
        )}
        {successMessage && (
          <Alert variant="success" message={successMessage} onClose={() => setSuccessMessage(null)} />
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-sm text-text-muted">Total Spots</div>
            <div className="text-2xl font-bold text-text-primary">{totalSpots}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-green-600">Available</div>
            <div className="text-2xl font-bold text-green-600">{availableSpots}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-red-600">Occupied</div>
            <div className="text-2xl font-bold text-red-600">{occupiedSpots}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-text-muted">Occupancy Rate</div>
            <div className="text-2xl font-bold text-text-primary">
              {totalSpots > 0 ? Math.round((occupiedSpots / totalSpots) * 100) : 0}%
            </div>
          </Card>
        </div>

        {/* Add Spots Form */}
        {showAddForm && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Add New Spots</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Input
                label="Prefix (Optional)"
                placeholder="e.g., A, B, P"
                value={spotPrefix}
                onChange={e => setSpotPrefix(e.target.value)}
              />
              <Input
                label="Start Number"
                type="number"
                min="1"
                value={spotStartNumber}
                onChange={e => setSpotStartNumber(e.target.value)}
              />
              <Input
                label="Number of Spots"
                type="number"
                min="1"
                max="100"
                value={spotCount}
                onChange={e => setSpotCount(e.target.value)}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Spot Type
                </label>
                <select
                  value={spotType}
                  onChange={e => setSpotType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="car">Car</option>
                  <option value="truck">Truck</option>
                  <option value="trailer">Trailer</option>
                  <option value="motorcycle">Motorcycle</option>
                  <option value="handicap">Handicap</option>
                  <option value="ev_charging">EV Charging</option>
                  <option value="reserved">Reserved</option>
                </select>
              </div>
              <Input
                label="Section/Row (Optional)"
                placeholder="e.g., North, A"
                value={spotSection}
                onChange={e => setSpotSection(e.target.value)}
              />
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleAddSpots}
                  disabled={addingSpots || !spotCount}
                  className="flex-1"
                >
                  {addingSpots ? 'Adding...' : 'Add Spots'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
            <p className="text-sm text-text-muted mt-3">
              Preview: {spotPrefix}{(parseInt(spotStartNumber) || 1).toString().padStart(3, '0')} to {spotPrefix}{((parseInt(spotStartNumber) || 1) + (parseInt(spotCount) || 1) - 1).toString().padStart(3, '0')}
            </p>
          </Card>
        )}

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by spot number or plate..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
            <div className="min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All Status</option>
                <option value="available">Available</option>
                <option value="occupied">Occupied</option>
                <option value="reserved">Reserved</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            <div className="min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All Types</option>
                <option value="car">Car</option>
                <option value="truck">Truck</option>
                <option value="trailer">Trailer</option>
                <option value="motorcycle">Motorcycle</option>
                <option value="handicap">Handicap</option>
                <option value="ev_charging">EV Charging</option>
                <option value="reserved">Reserved</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Spots Grid */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary">
              Parking Spots ({filteredSpots.length} of {totalSpots})
            </h3>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-primary-500 mr-2" />
              <span className="text-text-muted">Loading spots...</span>
            </div>
          ) : filteredSpots.length === 0 ? (
            <div className="text-center py-12">
              <Car size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-text-muted">
                {spots.length === 0 
                  ? 'No spots configured. Click "Add Spots" to create parking spots.'
                  : 'No spots match your filters.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
              {filteredSpots.map(spot => {
                const spotId = (spot as any).id || spot.spot_id;
                const spotStatus = spot.status || 'available';
                const spotNumber = spot.spot_number || '';
                const plate = (spot as any).current_vehicle_plate || spot.current_plate;
                const spotRow = (spot as any).row || spot.section;
                
                return (
                  <div
                    key={spotId}
                    onClick={() => handleSpotClick(spot)}
                    className={`p-4 rounded-lg border relative group flex flex-col items-center transition-all hover:shadow-md cursor-pointer ${
                      spotStatus === 'available' 
                        ? 'bg-green-50 border-green-200 hover:border-green-400 hover:bg-green-100' 
                        : spotStatus === 'occupied'
                        ? 'bg-red-50 border-red-200 hover:border-red-400 hover:bg-red-100'
                        : spotStatus === 'reserved'
                        ? 'bg-yellow-50 border-yellow-200 hover:border-yellow-400'
                        : 'bg-gray-50 border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteSpot(spotId); }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-sm shadow-md"
                      title="Delete spot"
                    >
                      ×
                    </button>
                    
                    <div className="flex items-center justify-center gap-1 mb-2">
                      {getSpotTypeIcon(spot.spot_type)}
                      <span className="font-bold text-base text-gray-900">{spotNumber}</span>
                    </div>
                    
                    <div className="text-xs text-gray-500 mb-2 capitalize">{spot.spot_type}</div>
                    
                    {spotRow && (
                      <div className="text-xs text-gray-400 mb-2">Row: {spotRow}</div>
                    )}
                    
                    <div className="mt-auto mb-2">
                      {getStatusBadge(spotStatus)}
                    </div>
                    
                    {plate && (
                      <div className="text-xs font-semibold text-blue-700 bg-blue-100 rounded px-2 py-1 w-full text-center truncate" 
                           title={plate}>
                        🚗 {plate}
                      </div>
                    )}
                    
                    {/* Click hint */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all">
                      <span className="text-xs text-white opacity-0 group-hover:opacity-100 font-medium bg-black bg-opacity-60 px-2 py-1 rounded">
                        {spotStatus === 'available' ? 'Click to Park' : spotStatus === 'occupied' ? 'Click to Release' : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Park Vehicle Modal */}
        {showParkModal && selectedSpotForPark && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  Park Vehicle in Spot {selectedSpotForPark.spot_number}
                </h3>
                <button
                  onClick={() => setShowParkModal(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      {getSpotTypeIcon(selectedSpotForPark.spot_type)}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{selectedSpotForPark.spot_number}</div>
                      <div className="text-xs text-gray-500 capitalize">{selectedSpotForPark.spot_type} spot</div>
                    </div>
                  </div>
                </div>

                <Input
                  label="Vehicle Plate"
                  placeholder="e.g., ABC-123"
                  value={parkVehiclePlate}
                  onChange={e => setParkVehiclePlate(e.target.value.toUpperCase())}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle Type
                  </label>
                  <select
                    value={parkVehicleType}
                    onChange={e => setParkVehicleType(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="car">Car</option>
                    <option value="motorcycle">Motorcycle</option>
                    <option value="truck">Truck</option>
                    <option value="trailer">Trailer</option>
                  </select>
                </div>

                <Input
                  label="Driver Name (Optional)"
                  placeholder="e.g., John Smith"
                  value={parkDriverName}
                  onChange={e => setParkDriverName(e.target.value)}
                />

                <Input
                  label="Notes (Optional)"
                  placeholder="Any additional notes..."
                  value={parkNotes}
                  onChange={e => setParkNotes(e.target.value)}
                />
              </div>

              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowParkModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleParkVehicle}
                  disabled={isParking}
                >
                  {isParking ? 'Parking...' : 'Park Vehicle'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Release Vehicle Modal */}
        {showReleaseModal && selectedSpotForRelease && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  Release Vehicle?
                </h3>
                <button
                  onClick={() => setShowReleaseModal(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                  {((selectedSpotForRelease as any).current_vehicle_plate || selectedSpotForRelease.current_plate) && (
                    <div className="flex items-center gap-3">
                      <Car size={20} className="text-red-600" />
                      <div>
                        <div className="text-xs text-gray-500">Plate</div>
                        <div className="font-semibold text-gray-900">{(selectedSpotForRelease as any).current_vehicle_plate || selectedSpotForRelease.current_plate}</div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center text-red-600">📍</div>
                    <div>
                      <div className="text-xs text-gray-500">Spot</div>
                      <div className="font-semibold text-gray-900">{selectedSpotForRelease.spot_number}</div>
                    </div>
                  </div>

                  {selectedSpotForRelease.occupied_since && (
                    <div className="flex items-center gap-3">
                      <Clock size={20} className="text-red-600" />
                      <div>
                        <div className="text-xs text-gray-500">Parked Since</div>
                        <div className="font-semibold text-gray-900">{formatTime(selectedSpotForRelease.occupied_since)}</div>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-sm text-gray-600">
                  Are you sure you want to release this vehicle from the parking spot?
                </p>
              </div>

              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowReleaseModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  onClick={handleReleaseSpot}
                  disabled={isReleasing}
                >
                  {isReleasing ? 'Releasing...' : 'Release'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Release Summary Modal */}
        {showReleaseSummary && releaseSummaryData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-green-600 flex items-center gap-2">
                  ✓ Vehicle Released
                </h3>
                <button
                  onClick={() => { setShowReleaseSummary(false); setReleaseSummaryData(null); }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                  {releaseSummaryData.vehicle_plate && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plate</span>
                      <span className="font-semibold text-gray-900">{releaseSummaryData.vehicle_plate}</span>
                    </div>
                  )}
                  
                  {releaseSummaryData.vehicle_type && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Type</span>
                      <span className="font-semibold text-gray-900 capitalize">{releaseSummaryData.vehicle_type}</span>
                    </div>
                  )}

                  {releaseSummaryData.driver_name && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Driver</span>
                      <span className="font-semibold text-gray-900">{releaseSummaryData.driver_name}</span>
                    </div>
                  )}

                  <hr className="my-2" />

                  <div className="flex justify-between">
                    <span className="text-gray-500">Entry</span>
                    <span className="font-semibold text-gray-900">{formatTime(releaseSummaryData.entry_time)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-500">Exit</span>
                    <span className="font-semibold text-gray-900">{formatTime(releaseSummaryData.exit_time)}</span>
                  </div>

                  <div className="flex justify-between bg-blue-50 -mx-4 px-4 py-2 mt-2">
                    <span className="text-blue-700 font-medium">Duration</span>
                    <span className="font-bold text-blue-700">
                      {parkingService.formatDuration(releaseSummaryData.duration_minutes)}
                      <span className="font-normal text-blue-600 ml-1">({releaseSummaryData.duration_minutes} min)</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 p-4 border-t bg-gray-50">
                <Button
                  className="flex-1"
                  onClick={() => { setShowReleaseSummary(false); setReleaseSummaryData(null); }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ParkingSpotsPage;
